// ============================================================
// Booking Wizard — 3 steps: dates → guest details → summary
// ============================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, differenceInDays, addDays, eachDayOfInterval, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import Header from '../components/Header';
import { getApartment, getBookedDates, checkAvailability, createBooking } from '../api/client';
import './BookingPage.css';

const STEPS = ['Выбор дат', 'Ваши данные', 'Подтверждение'];

export default function BookingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [apartment, setApartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Booked/blocked dates
  const [disabledDays, setDisabledDays] = useState([]);

  // Step 1: dates
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [numGuests, setNumGuests] = useState(1);
  const [availability, setAvailability] = useState(null);
  const [checkingAvail, setCheckingAvail] = useState(false);

  // Step 2: guest details
  const [form, setForm] = useState({
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    special_requests: '',
    payment_method: 'cash',
  });

  // Load apartment
  useEffect(() => {
    getApartment(id)
      .then(setApartment)
      .catch(() => setError('Квартира не найдена'))
      .finally(() => setLoading(false));
  }, [id]);

  // Load booked dates when apartment is loaded
  useEffect(() => {
    if (!apartment) return;

    getBookedDates(apartment.id)
      .then(data => {
        const ranges = data.booked_ranges || [];
        const allDays = [];

        ranges.forEach(r => {
          try {
            const start = parseISO(r.start);
            const end = parseISO(r.end);
            if (start && end && start < end) {
              // Block check-in day through last overnight day
              // Example: booking 27-29 blocks days 27, 28 (checkout day 29 is free)
              // Guest can still book 25-27 because backend uses strict overlap check
              // (their checkout=27 doesn't conflict with existing checkin=27)
              const days = eachDayOfInterval({ start, end: addDays(end, -1) });
              allDays.push(...days);
            }
          } catch {}
        });

        setDisabledDays(allDays);
      })
      .catch(() => {}); // silently fail
  }, [apartment]);

  // When dates selected, auto-check availability
  useEffect(() => {
    if (!range.from || !range.to || !apartment) return;

    setCheckingAvail(true);
    setAvailability(null);

    checkAvailability({
      apartment_id: apartment.id,
      check_in: format(range.from, 'yyyy-MM-dd'),
      check_out: format(range.to, 'yyyy-MM-dd'),
      num_guests: numGuests,
    })
      .then(setAvailability)
      .catch(() => setAvailability({ available: false, message: 'Ошибка проверки. Попробуйте ещё раз.' }))
      .finally(() => setCheckingAvail(false));
  }, [range.from, range.to, numGuests, apartment]); // eslint-disable-line

  const nights = range.from && range.to ? differenceInDays(range.to, range.from) : 0;

  // Handle date selection — prevent broken state on re-click
  function handleRangeSelect(newRange) {
    if (!newRange) {
      setRange({ from: undefined, to: undefined });
      setAvailability(null);
      return;
    }
    setRange(newRange);
    if (newRange.from && !newRange.to) {
      setAvailability(null);
    }
  }

  function handleFormChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const result = await createBooking({
        apartment_id: apartment.id,
        check_in: format(range.from, 'yyyy-MM-dd'),
        check_out: format(range.to, 'yyyy-MM-dd'),
        num_guests: numGuests,
        ...form,
      });
      navigate('/booking-confirmed', { state: { booking: result } });
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при оформлении брони. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;
  if (!apartment) return <div className="container" style={{ padding: 40 }}><div className="alert alert-error">Квартира не найдена</div></div>;

  // Today at midnight for calendar "from" date
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="booking-page">
      <Header showBack />

      <div className="container booking-container">
        {/* Progress bar */}
        <div className="steps-bar">
          {STEPS.map((s, i) => (
            <div key={s} className={`step-item ${i <= step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              <div className="step-circle">{i < step ? '✓' : i + 1}</div>
              <span className="step-label">{s}</span>
            </div>
          ))}
        </div>

        <div className="booking-layout">
          {/* Left: step content */}
          <div className="booking-form-area">

            {/* ── Step 0: Date selection ── */}
            {step === 0 && (
              <div className="card booking-card">
                <h2 className="booking-card-title">Выберите даты проживания</h2>

                {disabledDays.length > 0 && (
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--gray-500)', marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 12, height: 12, borderRadius: 3, background: '#fee2e2', border: '1px solid #fca5a5', display: 'inline-block' }} />
                      Занято
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 12, height: 12, borderRadius: 3, background: 'white', border: '1px solid var(--gray-300)', display: 'inline-block' }} />
                      Свободно
                    </span>
                  </div>
                )}

                <div className="calendar-wrap">
                  <DayPicker
                    mode="range"
                    selected={range}
                    onSelect={handleRangeSelect}
                    locale={ru}
                    fromDate={today}
                    numberOfMonths={1}
                    showOutsideDays={false}
                    modifiers={{ booked: disabledDays }}
                    modifiersClassNames={{ booked: 'rdp-day-booked' }}
                  />
                </div>

                {range.from && range.to && (
                  <div className="dates-selected">
                    <div className="date-chip">
                      <span className="date-label">Заезд</span>
                      <span className="date-value">{format(range.from, 'd MMMM', { locale: ru })}</span>
                    </div>
                    <div className="date-arrow">→</div>
                    <div className="date-chip">
                      <span className="date-label">Выезд</span>
                      <span className="date-value">{format(range.to, 'd MMMM', { locale: ru })}</span>
                    </div>
                    <div className="date-nights">{nights} {getNightForm(nights)}</div>
                  </div>
                )}

                {range.from && !range.to && (
                  <div className="alert alert-info" style={{ marginTop: 12 }}>
                    Теперь выберите дату выезда
                  </div>
                )}

                <div className="form-group" style={{ marginTop: 20 }}>
                  <label className="form-label">Количество гостей</label>
                  <select
                    className="form-input"
                    value={numGuests}
                    onChange={e => setNumGuests(Number(e.target.value))}
                  >
                    {Array.from({ length: apartment.max_guests }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>{n} {getNounForm(n, 'гость', 'гостя', 'гостей')}</option>
                    ))}
                  </select>
                </div>

                {checkingAvail && (
                  <div className="alert alert-info">
                    <span className="spinner" style={{ width: 16, height: 16 }} /> Проверяем доступность...
                  </div>
                )}

                {availability && !checkingAvail && (
                  <div className={`alert ${availability.available ? 'alert-success' : 'alert-error'}`}>
                    {availability.available
                      ? `✅ Квартира свободна! ${nights} ночей — от ${formatPrice(availability.total_price_rub)} ₽`
                      : `❌ ${availability.message || 'Эти даты заняты. Выберите другой период.'}`}
                  </div>
                )}

                <button
                  className="btn btn-primary btn-full btn-lg"
                  disabled={!availability?.available}
                  onClick={() => setStep(1)}
                  style={{ marginTop: 8 }}
                >
                  Продолжить →
                </button>
              </div>
            )}

            {/* ── Step 1: Guest details ── */}
            {step === 1 && (
              <div className="card booking-card">
                <h2 className="booking-card-title">Ваши контактные данные</h2>

                <div className="form-group">
                  <label className="form-label">Имя и фамилия *</label>
                  <input name="guest_name" className="form-input" value={form.guest_name} onChange={handleFormChange} placeholder="Иван Иванов" required />
                </div>

                <div className="form-group">
                  <label className="form-label">Номер телефона * (WhatsApp/Telegram)</label>
                  <input name="guest_phone" className="form-input" value={form.guest_phone} onChange={handleFormChange} placeholder="+7 (900) 123-45-67" type="tel" required />
                </div>

                <div className="form-group">
                  <label className="form-label">Email (необязательно)</label>
                  <input name="guest_email" className="form-input" value={form.guest_email} onChange={handleFormChange} placeholder="ivan@example.com" type="email" />
                </div>

                <div className="form-group">
                  <label className="form-label">Пожелания / особые запросы</label>
                  <textarea name="special_requests" className="form-input" value={form.special_requests} onChange={handleFormChange} placeholder="Поздний заезд, ранний выезд, детская кроватка..." rows={3} />
                </div>

                <div className="form-group">
                  <label className="form-label">Способ оплаты</label>
                  <div className="payment-options">
                    {[
                      { value: 'cash', label: '💵 Наличные', desc: 'При заселении' },
                      { value: 'card', label: '💳 Перевод на карту', desc: 'Реквизиты вышлем после подтверждения' },
                    ].map(opt => (
                      <label key={opt.value} className={`payment-option ${form.payment_method === opt.value ? 'selected' : ''}`}>
                        <input type="radio" name="payment_method" value={opt.value} checked={form.payment_method === opt.value} onChange={handleFormChange} />
                        <div>
                          <div className="pay-label">{opt.label}</div>
                          <div className="pay-desc">{opt.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="btn-row">
                  <button className="btn btn-secondary" onClick={() => setStep(0)}>← Назад</button>
                  <button className="btn btn-primary" disabled={!form.guest_name.trim() || !form.guest_phone.trim()} onClick={() => setStep(2)}>
                    К подтверждению →
                  </button>
                </div>
              </div>
            )}

            {/* ── Step 2: Summary & submit ── */}
            {step === 2 && (
              <div className="card booking-card">
                <h2 className="booking-card-title">Проверьте данные брони</h2>

                <div className="summary-grid">
                  <div className="summary-row"><span className="summary-label">Квартира</span><span className="summary-value">{apartment.name}</span></div>
                  <div className="summary-row"><span className="summary-label">Заезд</span><span className="summary-value">{format(range.from, 'd MMMM yyyy', { locale: ru })}</span></div>
                  <div className="summary-row"><span className="summary-label">Выезд</span><span className="summary-value">{format(range.to, 'd MMMM yyyy', { locale: ru })}</span></div>
                  <div className="summary-row"><span className="summary-label">Ночей</span><span className="summary-value">{nights}</span></div>
                  <div className="summary-row"><span className="summary-label">Гостей</span><span className="summary-value">{numGuests}</span></div>
                  <div className="summary-row"><span className="summary-label">Имя</span><span className="summary-value">{form.guest_name}</span></div>
                  <div className="summary-row"><span className="summary-label">Телефон</span><span className="summary-value">{form.guest_phone}</span></div>
                  <div className="summary-row"><span className="summary-label">Оплата</span><span className="summary-value">{form.payment_method === 'cash' ? 'Наличные' : 'Перевод на карту'}</span></div>
                  <div className="summary-row summary-total"><span className="summary-label">Итого (от)</span><span className="summary-value">{formatPrice(availability?.total_price_rub)} ₽</span></div>
                </div>

                <div className="alert alert-info" style={{ marginTop: 16, fontSize: 13 }}>
                  ℹ️ После отправки заявки мы свяжемся с вами по телефону для подтверждения и оплаты. Обычно в течение 30 минут.
                </div>

                {error && <div className="alert alert-error">{error}</div>}

                <div className="btn-row" style={{ marginTop: 20 }}>
                  <button className="btn btn-secondary" onClick={() => setStep(1)}>← Назад</button>
                  <button className="btn btn-primary btn-lg" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? <><span className="spinner" style={{ width: 18, height: 18 }} /> Отправляем...</> : '✅ Отправить заявку'}
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* Right: apartment summary sidebar */}
          <div className="booking-sidebar">
            <div className="card apt-summary">
              {apartment.photos?.[0] && (
                <img src={apartment.photos[0]} alt={apartment.name} className="apt-summary-img" />
              )}
              <div className="apt-summary-body">
                <h3>{apartment.name}</h3>
                <p className="apt-summary-addr">📍 {apartment.address}</p>
                <p className="apt-summary-price">от {formatPrice(apartment.price_per_night_rub)} ₽ / ночь</p>
                {range.from && range.to && (
                  <p className="apt-summary-total">{nights} {getNightForm(nights)} — от {formatPrice(nights * apartment.price_per_night_rub)} ₽</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatPrice(n) {
  return n ? new Intl.NumberFormat('ru-RU').format(n) : '—';
}

function getNightForm(n) {
  if (n % 100 >= 11 && n % 100 <= 19) return 'ночей';
  if (n % 10 === 1) return 'ночь';
  if (n % 10 >= 2 && n % 10 <= 4) return 'ночи';
  return 'ночей';
}

function getNounForm(n, f1, f2, f5) {
  if (n % 100 >= 11 && n % 100 <= 19) return f5;
  if (n % 10 === 1) return f1;
  if (n % 10 >= 2 && n % 10 <= 4) return f2;
  return f5;
}